import { takeLatest, put, call, select } from 'redux-saga/effects';
import { checkToken } from '../auth/sagas';
import { Urls } from '../../config/urls';
import { getCSVTask, getTask, getXlsxTask, postTask } from '../../services';
import {
  cancelCollaborationError,
  cancelCollaborationSuccess,
  exportSplitsError,
  exportSplitsSuccess,
  getSplitPaymentsError,
  getSplitPaymentsSuccess,
  performCollaborationActionError,
  performCollaborationActionSuccess,
  postCreateSplitPaymentError,
  postCreateSplitPaymentSuccess,
  SplitPaymentsActionType,
  validateMerchantCollaboratorError,
  validateMerchantCollaboratorSuccess,
} from './actions';
import { ExportType, PanelStore } from '../interfaces';

function* getSplitPayments(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  let params = `?page=${action.page || 0}&size=10`;
  const filterParamsString = action.filters ? new URLSearchParams(action.filters).toString() : '';
  params += filterParamsString ? `&${filterParamsString}` : '';

  const { response, error } = yield call(getTask, {
    url: Urls.api.splitCollaborations + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(getSplitPaymentsSuccess(response));
  } else {
    yield put(getSplitPaymentsError(error));
  }
}

function* postCreateSplitPayment(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(postTask, {
    url: Urls.api.createCollaboration,
    token: state.auth.token,
    body: state.splitPayments.createSplitPaymentPayload ?? {},
  });

  if (response) {
    yield put(postCreateSplitPaymentSuccess(response));
  } else {
    yield put(postCreateSplitPaymentError(error));
  }
}

function* validateMerchantCollaborator(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(postTask, {
    url: Urls.api.validateMerchantCollaborator,
    token: state.auth.token,
    body: { email: action.payload.email },
  });

  if (response) {
    yield put(validateMerchantCollaboratorSuccess(response));
  } else {
    yield put(validateMerchantCollaboratorError(error));
  }
}

function* performCollaborationAction(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(postTask, {
    url: Urls.api.splitCollaborationAction,
    token: state.auth.token,
    body: {
      action: action.action,
      collaborationId: action.id,
    },
  });

  if (response) {
    yield put(performCollaborationActionSuccess());
  } else {
    yield put(performCollaborationActionError(error));
  }
}

function* cancelSplitCollaboration(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(postTask, {
    url: Urls.api.cancelSplitCollaboration + `?collaboration_id=${action.id}`,
    token: state.auth.token,
    body: {},
  });

  if (response) {
    yield put(cancelCollaborationSuccess());
  } else {
    yield put(cancelCollaborationError(error));
  }
}

function* exportSplits(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const collaborationId = `collaboration_id=${action.collaborationId}`;
  const params = action.filter ? `?${action.filter}&${collaborationId}` : `?${collaborationId}`;
  const exportType: ExportType = action.exportType;
  const getTask = exportType === ExportType.CSV ? getCSVTask : getXlsxTask;
  const url = exportType === ExportType.CSV ? Urls.api.exportSplitsCsv : Urls.api.exportSplitsExcel;

  const { response, error } = yield call(getTask, {
    url: url + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(exportSplitsSuccess(response));
  } else {
    yield put(exportSplitsError(error));
  }
}

export function* exportSplitsSaga() {
  yield takeLatest(SplitPaymentsActionType.EXPORT_SPLITS, exportSplits);
}

export function* getSplitPaymentsSaga() {
  yield takeLatest(SplitPaymentsActionType.GET_SPLIT_PAYMENTS, getSplitPayments);
}

export function* postCreateSplitPaymentSaga() {
  yield takeLatest(SplitPaymentsActionType.POST_CREATE_SPLIT_PAYMENT, postCreateSplitPayment);
}

export function* validateMerchantCollaboratorSaga() {
  yield takeLatest(SplitPaymentsActionType.VALIDATE_MERCHANT_COLLABORATOR, validateMerchantCollaborator);
}

export function* performCollaborationActionSaga() {
  yield takeLatest(SplitPaymentsActionType.PERFORM_COLLABORATION_ACTION, performCollaborationAction);
}

export function* cancelCollaborationSaga() {
  yield takeLatest(SplitPaymentsActionType.CANCEL_COLLABORATION, cancelSplitCollaboration);
}
